// components/ImageExportModal.tsx
import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { X, Loader2, Download, ImagePlus } from '@/components/ui/lucide-memo';
import { DraftBound, ImageExportFileFormat, PrintExportOptions, Rect } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import RangeControl from './ui/RangeControl';
import Switch from './ui/Switch';
import { isLayerVectorized } from '#lib/svg-layer';
import { getDrawingAwareSvgLayerSource, getDrawingLayerSvgSource } from '#lib/drawing-layer';
import { collectVisibleImageExportLayers, isNativeSvgExportAvailable } from '#lib/image-export';
import { resolveStudioLayerTransform } from '#lib/studio-layer-transform';
import { selectOrderedWorkspaceLayerIdsSignature } from '#lib/workspace-card-selectors';
import LayerCanvas, { MAX_PREVIEW_RASTER_DIMENSION } from './LayerCanvas';
import { defaultPrintOptions, normalizePrintPreferences, PRINT_BLEED_VALUES, PRINT_DPI_VALUES, PRINT_ICC_PROFILES, PRINT_PRESETS, requestExportSaveTarget, resolvePrintDimensions, resolvePrintPreset, saveExportBlob, syncPrintDimensions, validatePrintExportOptions } from '#lib/print-export';
import {
  normalizeTimelineTrackPlaybackRate,
  readTimelineParamFlag,
  readTimelineParamNumber,
  readTimelineParamString,
  toFiniteTimelineMs,
  TIMELINE_AUDIO_GAIN_PARAM_KEY,
  TIMELINE_FADE_IN_RATIO_PARAM_KEY,
  TIMELINE_FADE_OUT_RATIO_PARAM_KEY,
  TIMELINE_MUTE_AUDIO_PARAM_KEY,
  TIMELINE_PLAYBACK_RATE_PARAM_KEY,
  TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY,
} from '#lib/workspace-timeline';
import type { TimelineCompositionStatus } from '#lib/timeline-video-types';
import { safeFileName } from '#lib/safeFileName';
import { signImageExport } from '#lib/provenance/signImageExport';
import { resolveLayerWorldPerPixel } from '#lib/studio-layer-density';
import { clampWithBound, parseDraftNumber, resolveImageExportSize, resolveOptimalExportMegapixels, sideLimitBound } from '#lib/export-size-draft';

type ExportTarget = 'area' | 'frame';
type ExportMode = 'image' | 'video' | 'auto';

interface ImageExportModalPayload {
  projectId?: string;
  workspaceId?: string;
  defaultTarget?: ExportTarget;
  exportMode?: ExportMode;
  /**
   * SOURCE-IMAGE mode (List outputs): export THIS asset instead of a canvas
   * render. Same menu, same format/size/quality controls — the pixel source
   * is the focused output. Pass exportMode: 'image' alongside it.
   */
  sourceImageUrl?: string;
  /** SOURCE-VIDEO mode (List outputs): export THIS clip instead of a timeline render. */
  sourceVideoUrl?: string;
  /** Filename seed for source media mode (e.g. the output's name). */
  sourceLabel?: string;
}

interface ImageExportModalProps {
  isOpen: boolean;
  projectId: string;
  payload?: ImageExportModalPayload | null;
  onClose: () => void;
}

interface ExportTimelineTrack {
  layerId: string;
  layerType: 'video' | 'image' | 'audio';
  sourceSrc: string;
  separatedFromLayerId: string | null;
  audioEnabled: boolean;
  audioGain: number;
  renderOrder: number;
  startMs: number;
  endMs: number | null;
  sourceInMs: number;
  playbackRate: number;
  fadeInRatio: number;
  fadeOutRatio: number;
  rotationDeg: number;
  flipX: boolean;
  flipY: boolean;
  opacity: number;
  rect: Rect;
  scale: number;
}

type VideoProcessingStep = 'analyzing' | 'pulling' | 'building' | 'finalizing' | 'done';
type VideoOutputFormat = 'mp4' | 'mov';
type VideoQualityPreset = 'draft' | 'balanced' | 'high';
const DEFAULT_VIDEO_QUALITY_PRESET: VideoQualityPreset = 'high';
const IMAGE_EXPORT_FORMATS: readonly ImageExportFileFormat[] = ['png', 'webp', 'jpeg', 'svg', 'pdf'];

const translations = {
  en: {
    title: 'Export Image',
    titleVideo: 'Export Video',
    close: 'Close',
    exportName: 'Export Name',
    exportMode: 'Export mode',
    webImage: 'Web image',
    printGrandFormat: 'Print Grand Format',
    exportTarget: 'Export Target',
    generationArea: 'Generation Area',
    workspaceFrame: 'Workspace Frame',
    format: 'Format',
    megapixels: 'Megapixels',
    width: 'Width (px)',
    height: 'Height (px)',
    quality: 'Quality',
    alpha: 'Preserve Alpha',
    annotations: 'Include Annotations',
    standardPreset: 'Standard preset',
    widthCm: 'Width (cm)',
    heightCm: 'Height (cm)',
    dpi: 'DPI',
    iccProfile: 'ICC profile',
    bleed: 'Bleed',
    disabled: 'Disabled',
    custom: 'Custom',
    preview: 'Preview',
    lightPreview: '(reduced resolution)',
    preparing: 'Preparing preview...',
    noPreview: 'Preview unavailable.',
    download: 'Export',
    openInNewProject: 'Open in a new project',
    noVisibleLayers: 'No visible layers found in this workspace.',
    exportStart: 'Start (s)',
    exportEnd: 'End (s)',
    exportTillEnd: 'Till End',
    boundMax: 'Max',
    boundMin: 'Min',
    optimal: 'Optimal',
    optimalBase: 'Native resolution of the layers covering most of the export',
    optimalPatch: 'Raised to a small denser area, under 8K',
    optimalCapped: 'Capped at 8K: only small areas are denser',
    optimalSparse: 'No layer covers most of the export: its sharpest small area, up to 8K',
    optimalNative: 'Native size of the image',
    optimalUnavailable: 'No layer to measure',
    videoOutputFps: 'FPS',
    videoOutputFormat: 'Format',
    videoQuality: 'Quality',
    qualityDraft: 'Draft',
    qualityBalanced: 'Balanced',
    qualityHigh: 'High',
    exportSound: 'Include Sound In Export',
    processingTitle: 'Processing video export',
    processingAnalyzing: 'Analyzing timeline',
    processingPullingTracks: 'Pulling tracks',
    processingBuilding: 'Building final video',
    processingFinalizing: 'Finalizing export',
    processingDone: 'Done',
  },
  fr: {
    title: "Exporter l’image",
    titleVideo: 'Exporter la vidéo',
    close: 'Fermer',
    exportName: "Nom d’export",
    exportMode: "Mode d’export",
    webImage: 'Image web',
    printGrandFormat: 'Impression grand format',
    exportTarget: "Zone d’export",
    generationArea: 'Zone de génération',
    workspaceFrame: 'Espace de travail entier',
    format: 'Format',
    megapixels: 'Mégapixels',
    width: 'Largeur (px)',
    height: 'Hauteur (px)',
    quality: 'Qualité',
    alpha: 'Conserver la transparence',
    annotations: 'Inclure les annotations',
    standardPreset: 'Préréglage standard',
    widthCm: 'Largeur (cm)',
    heightCm: 'Hauteur (cm)',
    dpi: 'DPI',
    iccProfile: 'Profil ICC',
    bleed: 'Fond perdu',
    disabled: 'Désactivé',
    custom: 'Personnalisé',
    preview: 'Aperçu',
    lightPreview: '(résolution réduite)',
    preparing: "Préparation de l’aperçu…",
    noPreview: 'Aperçu indisponible.',
    download: 'Exporter',
    openInNewProject: 'Ouvrir dans un nouveau projet',
    noVisibleLayers: 'Aucune couche visible dans cet espace.',
    exportStart: 'Début (s)',
    exportEnd: 'Fin (s)',
    exportTillEnd: 'Jusqu’à la fin',
    boundMax: 'Max.',
    boundMin: 'Min.',
    optimal: 'Optimal',
    optimalBase: 'Résolution native des calques qui couvrent l’essentiel de l’export',
    optimalPatch: 'Relevé jusqu’à une petite zone plus dense, sous 8K',
    optimalCapped: 'Plafonné à 8K : seules de petites zones sont plus denses',
    optimalSparse: 'Aucun calque ne couvre l’essentiel de l’export : sa petite zone la plus nette, jusqu’à 8K',
    optimalNative: 'Taille native de l’image',
    optimalUnavailable: 'Aucun calque à mesurer',
    videoOutputFps: 'FPS',
    videoOutputFormat: 'Format',
    videoQuality: 'Qualité',
    qualityDraft: 'Brouillon',
    qualityBalanced: 'Equilibre',
    qualityHigh: 'Elevee',
    exportSound: "Inclure le son à l’export",
    processingTitle: "Traitement de l’export vidéo",
    processingAnalyzing: 'Analyse de la timeline',
    processingPullingTracks: 'Chargement des pistes',
    processingBuilding: 'Création de la vidéo finale',
    processingFinalizing: 'Finalisation de l’export',
    processingDone: 'Terminé',
  },
} as const;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const roundTo = (value: number, decimals: number) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const MIN_EXPORT_SIDE = 64;
const MAX_EXPORT_SIDE = 32768;
const MIN_VIDEO_EXPORT_SIDE = 2;
const MAX_VIDEO_EXPORT_SIDE = 4096;
const MIN_EXPORT_MEGAPIXELS = 0.000001;
const MIN_QUALITY_PERCENT = 80;
const DEFAULT_QUALITY_PERCENT = 99;
const SOURCE_PREVIEW_TARGET_PIXELS = 2_000_000;
const TOP_LAYER_RENDER_ORDER_OFFSET = 1_000_000;
const AUDIO_GAIN_MAX = 8;
const DRAFT_COMMIT_IDLE_MS = 1000;
const DEFAULT_IMAGE_ASPECT_RATIO = 3 / 2;
const MIN_PRINT_CM = 0.01;
const MIN_VIDEO_FPS = 8;
const MAX_VIDEO_FPS = 60;
const MIN_VIDEO_RANGE_SEC = 0.1;

type NumericFieldKey =
  | 'megapixels'
  | 'width'
  | 'height'
  | 'printWidthCm'
  | 'printHeightCm'
  | 'videoWidth'
  | 'videoHeight'
  | 'videoFps'
  | 'videoStart'
  | 'videoEnd';

const formatBoundNote = (
  bound: DraftBound,
  limit: number,
  unit: string,
  labels: { boundMax: string; boundMin: string },
): string | null => (
  bound ? `${bound === 'max' ? labels.boundMax : labels.boundMin} ${limit} ${unit}` : null
);
const formatMegapixels = (value: number) => String(roundTo(value, 6));
const formatCm = (value: number) => String(roundTo(value, 2));
const formatImageSide = (value: number) => String(clamp(Math.round(value), MIN_EXPORT_SIDE, MAX_EXPORT_SIDE));

interface DraftNumberFieldProps {
  field: NumericFieldKey;
  label: string;
  value: string;
  draft: string | null;
  preview?: string;
  note?: string | null;
  placeholder?: string;
  action?: React.ReactNode;
  onDraftChange: (field: NumericFieldKey, text: string | null) => void;
  onCommit: (field: NumericFieldKey, text: string) => void;
}

export const DraftNumberField = memo(({ field, label, value, draft, preview, note, placeholder, action, onDraftChange, onCommit }: DraftNumberFieldProps) => {
  const idleTimerRef = useRef<number | null>(null);
  // The idle commit fires up to a second after its keystroke: it must reach the handler of that
  // moment, bound to the current aspect ratio and limits, not the one captured at the keystroke.
  const latestOnCommitRef = useRef(onCommit);
  useEffect(() => {
    latestOnCommitRef.current = onCommit;
  });
  const isPending = draft !== null;

  const stopIdleTimer = useCallback(() => {
    if (idleTimerRef.current === null) return;
    window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = null;
  }, []);

  useEffect(() => () => {
    if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current);
  }, []);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    stopIdleTimer();
    onDraftChange(field, text);
    idleTimerRef.current = window.setTimeout(() => {
      idleTimerRef.current = null;
      latestOnCommitRef.current(field, text);
    }, DRAFT_COMMIT_IDLE_MS);
  }, [field, onDraftChange, stopIdleTimer]);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    stopIdleTimer();
    if (isPending) onCommit(field, event.currentTarget.value);
  }, [field, isPending, onCommit, stopIdleTimer]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isPending) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      stopIdleTimer();
      onCommit(field, event.currentTarget.value);
    } else if (event.key === 'Escape') {
      // Without this the modal's window-level Escape would close the dialog.
      event.preventDefault();
      event.stopPropagation();
      stopIdleTimer();
      onDraftChange(field, null);
    }
  }, [field, isPending, onCommit, onDraftChange, stopIdleTimer]);

  const inputId = useId();
  const isPreview = draft === null && preview !== undefined;
  // The action sits outside the <label>: a label without htmlFor would control the action button.
  return (
    <div className="text-xs text-white/70">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={inputId}>{label}</label>
        {action}
      </div>
      <input
        id={inputId}
        type="text"
        inputMode="decimal"
        value={draft ?? preview ?? value}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={`mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm outline-none transition-colors duration-150 ease-out focus:border-[#3B82F6] ${isPreview ? 'italic text-white/45' : 'text-white'}`}
      />
      {/* Always rendered so a note appearing on blur cannot move the Export button under the pointer. */}
      <span className="mt-1 block h-4 text-[11px] leading-4 text-white/45">{note}</span>
    </div>
  );
});
DraftNumberField.displayName = 'DraftNumberField';

export const resolveExportPreviewFit = (
  targetRect: Pick<Rect, 'w' | 'h'> | null | undefined,
  containerWidth: number,
  containerHeight: number,
): { scale: number; width: number; height: number } | null => {
  const targetWidth = Number(targetRect?.w);
  const targetHeight = Number(targetRect?.h);
  const safeContainerWidth = Number.isFinite(containerWidth) && containerWidth > 0 ? containerWidth : 0;
  const safeContainerHeight = Number.isFinite(containerHeight) && containerHeight > 0 ? containerHeight : 0;
  if (!Number.isFinite(targetWidth) || !Number.isFinite(targetHeight) || targetWidth <= 0 || targetHeight <= 0) {
    return null;
  }
  if (safeContainerWidth <= 0 || safeContainerHeight <= 0) {
    return null;
  }

  const scale = Math.min(safeContainerWidth / targetWidth, safeContainerHeight / targetHeight);
  if (!Number.isFinite(scale) || scale <= 0) return null;
  return { scale, width: targetWidth, height: targetHeight };
};


const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const isImageExportFileFormat = (value: unknown): value is ImageExportFileFormat => (
  typeof value === 'string' && IMAGE_EXPORT_FORMATS.includes(value as ImageExportFileFormat)
);

const normalizeTrackEffectRatio = (value: unknown): number => {
  const parsed = toFiniteTimelineMs(value);
  if (parsed === null) return 0;
  return clamp(parsed, 0, 1);
};

const normalizeLayerOpacity = (value: unknown): number => {
  const parsed = toFiniteTimelineMs(value);
  if (parsed === null) return 1;
  return clamp(parsed, 0, 1);
};

const normalizeVideoDimension = (value: number): number => {
  const rounded = Math.max(MIN_VIDEO_EXPORT_SIDE, Math.round(value));
  return rounded % 2 === 0 ? rounded : rounded - 1;
};

const clampVideoExportDimension = (value: number): number => (
  normalizeVideoDimension(clamp(value, MIN_VIDEO_EXPORT_SIDE, MAX_VIDEO_EXPORT_SIDE))
);

const resolveVideoExportSizeFromWidth = (widthRaw: number, aspectRatio: number): { width: number; height: number } => {
  const safeAspectRatio = aspectRatio > 0 ? aspectRatio : 16 / 9;
  let width = clamp(widthRaw, MIN_VIDEO_EXPORT_SIDE, MAX_VIDEO_EXPORT_SIDE);
  let height = width / safeAspectRatio;
  if (height > MAX_VIDEO_EXPORT_SIDE) {
    height = MAX_VIDEO_EXPORT_SIDE;
    width = height * safeAspectRatio;
  } else if (height < MIN_VIDEO_EXPORT_SIDE) {
    height = MIN_VIDEO_EXPORT_SIDE;
    width = height * safeAspectRatio;
  }
  return {
    width: clampVideoExportDimension(width),
    height: clampVideoExportDimension(height),
  };
};

const resolveVideoExportSizeFromHeight = (heightRaw: number, aspectRatio: number): { width: number; height: number } => {
  const safeAspectRatio = aspectRatio > 0 ? aspectRatio : 16 / 9;
  let height = clamp(heightRaw, MIN_VIDEO_EXPORT_SIDE, MAX_VIDEO_EXPORT_SIDE);
  let width = height * safeAspectRatio;
  if (width > MAX_VIDEO_EXPORT_SIDE) {
    width = MAX_VIDEO_EXPORT_SIDE;
    height = width / safeAspectRatio;
  } else if (width < MIN_VIDEO_EXPORT_SIDE) {
    width = MIN_VIDEO_EXPORT_SIDE;
    height = width / safeAspectRatio;
  }
  return {
    width: clampVideoExportDimension(width),
    height: clampVideoExportDimension(height),
  };
};

const resolveVideoSizeDraft = (field: 'videoWidth' | 'videoHeight', value: number, aspectRatio: number | null) => {
  const safeAspectRatio = aspectRatio && aspectRatio > 0 ? aspectRatio : 16 / 9;
  return field === 'videoWidth'
    ? { ...resolveVideoExportSizeFromWidth(value, safeAspectRatio), bound: sideLimitBound(value, value / safeAspectRatio, MIN_VIDEO_EXPORT_SIDE, MAX_VIDEO_EXPORT_SIDE) }
    : { ...resolveVideoExportSizeFromHeight(value, safeAspectRatio), bound: sideLimitBound(value * safeAspectRatio, value, MIN_VIDEO_EXPORT_SIDE, MAX_VIDEO_EXPORT_SIDE) };
};

const clampPrintCm = (valueCm: number) => clampWithBound(valueCm, MIN_PRINT_CM, Number.POSITIVE_INFINITY);

const resolvePrintSizeDraft = (
  current: Pick<PrintExportOptions, 'widthCm' | 'heightCm'>,
  field: 'printWidthCm' | 'printHeightCm',
  clampedValueCm: number,
) => syncPrintDimensions({
  widthCm: current.widthCm,
  heightCm: current.heightCm,
  changedDimension: field === 'printWidthCm' ? 'widthCm' : 'heightCm',
  valueCm: clampedValueCm,
});

const estimateBytesFromSettings = ({
  pixelCount,
  format,
  qualityPercent,
  alpha,
  annotations,
}: {
  pixelCount: number;
  format: ImageExportFileFormat | 'tiff';
  qualityPercent: number;
  alpha: boolean;
  annotations: boolean;
}): number => {
  if (!Number.isFinite(pixelCount) || pixelCount <= 0) return 0;
  const qualityFactor = clamp(qualityPercent, MIN_QUALITY_PERCENT, 100) / 100;

  let bytesPerPixel = 1;
  if (format === 'tiff') {
    // LZW RGB TIFF is lossless but usually smaller than raw RGB.
    bytesPerPixel = 1.5;
  } else if (format === 'jpeg') {
    // JPEG is typically compact for photographic content.
    bytesPerPixel = 0.22 + qualityFactor * 0.95;
  } else if (format === 'pdf') {
    // PDF embeds a flattened JPEG page plus small document overhead.
    bytesPerPixel = 0.28 + qualityFactor * 0.9;
  } else if (format === 'webp') {
    // WebP is generally smaller than JPEG at the same visual quality.
    bytesPerPixel = 0.14 + qualityFactor * 0.7 + (alpha ? 0.08 : 0);
  } else if (format === 'svg') {
    // Native vector SVG size is driven by paths, not pixels; this is a conservative UI estimate.
    bytesPerPixel = 0.05;
  } else {
    // PNG is lossless and often significantly larger.
    bytesPerPixel = alpha ? 2.3 : 1.9;
  }

  if (annotations) {
    bytesPerPixel *= 1.05;
  }

  return Math.max(1, Math.round(pixelCount * bytesPerPixel));
};

const ImageExportModal: React.FC<ImageExportModalProps> = ({ isOpen, projectId, payload, onClose }) => {
  const { kernel } = useKernel();
  const uiWorkspaceId = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    return typeof runtime?.UIWorkspaceId === 'string' ? runtime.UIWorkspaceId.trim() : '';
  });
  const projectName = useKernelStore((state) => {
    const projectRow = state.inventory.Project.registry[projectId];
    return typeof projectRow?.name === 'string' ? projectRow.name.trim() : '';
  });
  const userPreferences = useKernelStore((state) => state.userPreferences);
  const lang = useKernelStore((state) => state.language) || 'en';
  const t = translations[lang];

  const resolvedWorkspaceId = payload?.workspaceId || uiWorkspaceId;
  const workspace = useKernelStore((state) =>
    state.inventory.StudioWorkspace.registry[resolvedWorkspaceId] ?? null
  );
  const layerRegistry = useKernelStore((state) => state.inventory.StudioLayer.registry);
  const workspaceId = workspace?.id ?? null;
  const workspaceFrameRect = workspace?.frameRect ?? null;
  const workspaceGenerationRect = workspace?.generationRect ?? null;
  const orderedWorkspaceLayerIdsSignature = useKernelStore((state) => (
    selectOrderedWorkspaceLayerIdsSignature(state, workspaceId ?? '')
  ));
  const orderedWorkspaceLayerIds = useMemo(() => (
    orderedWorkspaceLayerIdsSignature ? orderedWorkspaceLayerIdsSignature.split(',') : []
  ), [orderedWorkspaceLayerIdsSignature]);
  const visibleWorkspaceLayerIds = useMemo(() => (
    orderedWorkspaceLayerIds.filter((layerId) => layerRegistry[layerId]?.visible !== false)
  ), [layerRegistry, orderedWorkspaceLayerIds]);
  const hasReducedResolutionPreview = useMemo(() => (
    visibleWorkspaceLayerIds.some((layerId) => {
      const imageData = layerRegistry[layerId]?.imageData;
      if (!imageData) return false;
      return Math.max(imageData.width, imageData.height) > MAX_PREVIEW_RASTER_DIMENSION;
    })
  ), [layerRegistry, visibleWorkspaceLayerIds]);

  // SOURCE-IMAGE mode: the pixel source is a specific asset (a List output),
  // not a canvas render. The workspace still resolves (dims plumbing,
  // filenames) but preview/export read the asset itself.
  const sourceImageUrl =
    typeof payload?.sourceImageUrl === 'string' && payload.sourceImageUrl.trim().length > 0
      ? payload.sourceImageUrl.trim()
      : null;
  const sourceVideoUrl =
    typeof payload?.sourceVideoUrl === 'string' && payload.sourceVideoUrl.trim().length > 0
      ? payload.sourceVideoUrl.trim()
      : null;
  const isSourceImageMode = Boolean(sourceImageUrl);
  const isSourceVideoMode = Boolean(sourceVideoUrl);
  const [sourceImageDims, setSourceImageDims] = useState<{ w: number; h: number } | null>(null);
  const [sourceVideoMeta, setSourceVideoMeta] = useState<{ width: number; height: number; durationMs: number } | null>(null);
  useEffect(() => {
    if (!isOpen || !sourceImageUrl) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset source dimensions when the modal leaves source-image mode.
      setSourceImageDims(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch(sourceImageUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const bitmap = await createImageBitmap(blob);
        if (!cancelled) setSourceImageDims({ w: bitmap.width, h: bitmap.height });
        if (typeof bitmap.close === 'function') bitmap.close();
      } catch {
        if (!cancelled) setSourceImageDims(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, sourceImageUrl]);

  useEffect(() => {
    if (!isOpen || !sourceVideoUrl) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset source dimensions when the modal leaves source-video mode.
      setSourceVideoMeta(null);
      return;
    }
    let cancelled = false;
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    const cleanup = () => {
      video.removeAttribute('src');
      video.load();
    };
    video.onloadedmetadata = () => {
      if (cancelled) return;
      setSourceVideoMeta({
        width: video.videoWidth || 1280,
        height: video.videoHeight || 720,
        durationMs: Number.isFinite(video.duration) && video.duration > 0 ? Math.round(video.duration * 1000) : 1000,
      });
      cleanup();
    };
    video.onerror = () => {
      if (!cancelled) setSourceVideoMeta(null);
      cleanup();
    };
    video.src = sourceVideoUrl;
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [isOpen, sourceVideoUrl]);

  const [target, setTarget] = useState<ExportTarget>('area');
  const [format, setFormat] = useState<ImageExportFileFormat>('png');
  const [isPrintMode, setIsPrintMode] = useState(false);
  const [printTarget, setPrintTarget] = useState<ExportTarget>('area');
  const [printOptions, setPrintOptions] = useState<PrintExportOptions>(() => defaultPrintOptions());
  const [printProgressPercent, setPrintProgressPercent] = useState(0);
  const [printError, setPrintError] = useState<string | null>(null);
  const [megapixels, setMegapixels] = useState<number>(6);
  const [numericDraft, setNumericDraft] = useState<{ field: NumericFieldKey; text: string } | null>(null);
  const [numericNote, setNumericNote] = useState<{ field: NumericFieldKey; text: string } | null>(null);
  const [qualityPercent, setQualityPercent] = useState<number>(DEFAULT_QUALITY_PERCENT);
  const [preserveAlpha, setPreserveAlpha] = useState<boolean>(true);
  const [includeAnnotations, setIncludeAnnotations] = useState<boolean>(false);

  const [isSourcePreviewLoading, setIsSourcePreviewLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [sourcePreviewUrl, setSourcePreviewUrl] = useState<string | null>(null);
  const [sourcePreviewError, setSourcePreviewError] = useState<string | null>(null);
  const [sourcePreviewTargetSize, setSourcePreviewTargetSize] = useState<{ width: number; height: number } | null>(null);
  const [previewViewportSize, setPreviewViewportSize] = useState({ width: 0, height: 0 });
  const [videoRangeStartMs, setVideoRangeStartMs] = useState<number | null>(null);
  const [videoRangeEndMs, setVideoRangeEndMs] = useState<number | null>(null);
  const [videoRangeTillEnd, setVideoRangeTillEnd] = useState<boolean>(true);
  const [videoOutputWidth, setVideoOutputWidth] = useState<number | null>(null);
  const [videoOutputHeight, setVideoOutputHeight] = useState<number | null>(null);
  const [videoOutputFps, setVideoOutputFps] = useState<number>(30);
  const [videoOutputFormat, setVideoOutputFormat] = useState<VideoOutputFormat>('mp4');
  const [videoQualityPreset, setVideoQualityPreset] = useState<VideoQualityPreset>(DEFAULT_VIDEO_QUALITY_PRESET);
  const [backendProgressPercent, setBackendProgressPercent] = useState<number>(0);
  const [videoProcessingStep, setVideoProcessingStep] = useState<VideoProcessingStep>('analyzing');
  const [videoTracksPulled, setVideoTracksPulled] = useState<number>(0);
  const [videoTracksTotal, setVideoTracksTotal] = useState<number>(0);
  const [exportSoundEnabled, setExportSoundEnabled] = useState<boolean>(true);
  const [customExportName, setCustomExportName] = useState<string>('');

  const sourcePreviewUrlRef = useRef<string | null>(null);
  const sourcePreviewRequestIdRef = useRef<number>(0);
  const previewViewportRef = useRef<HTMLDivElement | null>(null);
  const previewResizeHandlesPortalHostRef = useRef<HTMLDivElement | null>(null);
  const modalInitializationKeyRef = useRef<string | null>(null);
  const timelineExportInFlightRef = useRef(false);
  const timelineExportAbortControllerRef = useRef<AbortController | null>(null);
  const printExportAbortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(false);

  const visibleTimelineTracks = useMemo<ExportTimelineTrack[]>(() => {
    const layerIds = Array.isArray(workspace?.layerIds) ? workspace.layerIds : [];
    const collectedTracks = layerIds
      .map((layerId, index) => {
        const layer = layerRegistry[layerId];
        if (!layer || layer.visible === false) return null;
        const isVideo = layer.type === 'video';
        const isAudio = layer.type === 'audio';
        const isImage = !isVideo && !isAudio;
        if (!isVideo && !isAudio && !isImage) return null;
        const imageSource = isImage
          ? (isLayerVectorized(layer)
              ? getDrawingAwareSvgLayerSource({
                  imageSrc: typeof layer.imageSrc === 'string' ? layer.imageSrc : undefined,
                  params: layer.params ?? undefined,
                })
              : (getDrawingLayerSvgSource(layer.params)?.source ?? (typeof layer.imageSrc === 'string' ? layer.imageSrc.trim() : '')))
          : '';
        const source = (isVideo || isAudio)
          ? (typeof layer.videoSrc === 'string' ? layer.videoSrc.trim() : '')
          : (imageSource ?? '');
        if (!source) return null;
        const startMs = Math.max(0, toFiniteTimelineMs(layer.timelineStartMs) ?? 0);
        const endMsRaw = toFiniteTimelineMs(layer.timelineEndMs);
        const endMs = endMsRaw !== null && endMsRaw > startMs ? endMsRaw : null;
        const sourceInMs = (isVideo || isAudio) ? Math.max(0, toFiniteTimelineMs(layer.timelineSourceInMs) ?? 0) : 0;
        const mutedInTimeline = isVideo && readTimelineParamFlag(layer.params, TIMELINE_MUTE_AUDIO_PARAM_KEY);
        const audioGainRaw = readTimelineParamNumber(layer.params, TIMELINE_AUDIO_GAIN_PARAM_KEY);
        const audioGain = audioGainRaw !== null ? clamp(audioGainRaw, 0, AUDIO_GAIN_MAX) : 1;
        const separatedFromLayerId = isAudio
          ? readTimelineParamString(layer.params, TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY)
          : null;
        const renderOrder = typeof layer.renderOrder === 'number' && Number.isFinite(layer.renderOrder)
          ? layer.renderOrder
          : index;
        const isTopLayer = Reflect.get(layer, 'top-layer') === true || layer.topLayer === true;
        const fadeInRatio = !isAudio
          ? normalizeTrackEffectRatio(readTimelineParamNumber(layer.params, TIMELINE_FADE_IN_RATIO_PARAM_KEY))
          : 0;
        const fadeOutRatio = !isAudio
          ? normalizeTrackEffectRatio(readTimelineParamNumber(layer.params, TIMELINE_FADE_OUT_RATIO_PARAM_KEY))
          : 0;
        const playbackRate = (isVideo || isAudio)
          ? normalizeTimelineTrackPlaybackRate(readTimelineParamNumber(layer.params, TIMELINE_PLAYBACK_RATE_PARAM_KEY))
          : 1;
        const transform = !isAudio
          ? resolveStudioLayerTransform(layer.params)
          : { rotationDeg: 0, flipX: false, flipY: false };
        return {
          layerId: layer.id,
          layerType: isAudio ? 'audio' : (isVideo ? 'video' : 'image'),
          sourceSrc: source,
          separatedFromLayerId,
          audioEnabled: isAudio ? true : (isVideo ? !mutedInTimeline : false),
          audioGain: isAudio ? audioGain : 1,
          renderOrder: isTopLayer ? (renderOrder + TOP_LAYER_RENDER_ORDER_OFFSET) : renderOrder,
          startMs,
          endMs,
          sourceInMs,
          playbackRate,
          fadeInRatio,
          fadeOutRatio,
          rotationDeg: transform.rotationDeg,
          flipX: transform.flipX,
          flipY: transform.flipY,
          opacity: !isAudio ? normalizeLayerOpacity(layer.opacity) : 1,
          rect: {
            x: Number.isFinite(layer.rect?.x) ? Number(layer.rect.x) : 0,
            y: Number.isFinite(layer.rect?.y) ? Number(layer.rect.y) : 0,
            w: Number.isFinite(layer.rect?.w) ? Number(layer.rect.w) : 0,
            h: Number.isFinite(layer.rect?.h) ? Number(layer.rect.h) : 0,
          },
          scale: typeof layer.scale === 'number' && Number.isFinite(layer.scale) ? layer.scale : 1,
        };
      })
      .filter((track): track is ExportTimelineTrack => Boolean(track))
      .sort((left, right) => {
        if (left.renderOrder !== right.renderOrder) return left.renderOrder - right.renderOrder;
        return left.layerId.localeCompare(right.layerId);
      });
    const associatedVideoLayerIdsFromVisibleAudio = new Set<string>(
      collectedTracks
        .filter((track) => track.layerType === 'audio')
        .map((track) => track.separatedFromLayerId)
        .filter((layerId): layerId is string => typeof layerId === 'string' && layerId.trim().length > 0)
    );
    return collectedTracks.map((track) => {
      if (track.layerType !== 'video') return track;
      const shouldMuteFromAssociatedAudio = associatedVideoLayerIdsFromVisibleAudio.has(track.layerId);
      if (!shouldMuteFromAssociatedAudio) return track;
      return {
        ...track,
        audioEnabled: false,
      };
    });
  }, [layerRegistry, workspace?.layerIds]);

  const videoTrackPayload = useMemo(() => {
    return visibleTimelineTracks.map((track) => ({
      layerId: track.layerId,
      layerType: track.layerType,
      sourceSrc: track.sourceSrc,
      videoSrc: track.layerType === 'video' ? track.sourceSrc : null,
      imageSrc: track.layerType === 'image' ? track.sourceSrc : null,
      audioEnabled: track.audioEnabled,
      audioGain: track.audioGain,
      renderOrder: track.renderOrder,
      startMs: Math.max(0, Math.round(track.startMs)),
      endMs: typeof track.endMs === 'number' && Number.isFinite(track.endMs)
        ? Math.max(0, Math.round(track.endMs))
        : null,
      sourceInMs: Math.max(0, Math.round(track.sourceInMs)),
      playbackRate: track.playbackRate,
      fadeInRatio: clamp(track.fadeInRatio, 0, 1),
      fadeOutRatio: clamp(track.fadeOutRatio, 0, 1),
      rotationDeg: track.rotationDeg,
      flipX: track.flipX,
      flipY: track.flipY,
      opacity: clamp(track.opacity, 0, 1),
      rect: {
        x: Number.isFinite(track.rect.x) ? track.rect.x : 0,
        y: Number.isFinite(track.rect.y) ? track.rect.y : 0,
        w: Number.isFinite(track.rect.w) ? track.rect.w : 0,
        h: Number.isFinite(track.rect.h) ? track.rect.h : 0,
      },
      scale: typeof track.scale === 'number' && Number.isFinite(track.scale) ? track.scale : 1,
    }));
  }, [visibleTimelineTracks]);

  const videoTrackSignature = useMemo(() => JSON.stringify(videoTrackPayload), [videoTrackPayload]);
  const stableVideoTrackPayload = useMemo(
    () => JSON.parse(videoTrackSignature) as typeof videoTrackPayload,
    [videoTrackSignature]
  );
  const hasTimelineMedia = visibleTimelineTracks.some(
    (track) => track.layerType === 'video' || track.layerType === 'audio'
  );
  const requestedExportMode = payload?.exportMode ?? 'auto';
  const isVideoExportMode = isSourceVideoMode || (requestedExportMode === 'video'
    ? hasTimelineMedia
    : (requestedExportMode === 'auto' ? hasTimelineMedia : false));
  const nativeSvgExportAvailable = useMemo(() => (
    isNativeSvgExportAvailable({
      isSourceImageMode,
      hasTimelineMedia,
      includeAnnotations,
      visibleImageExportLayers: collectVisibleImageExportLayers(
        Array.isArray(workspace?.layerIds) ? workspace.layerIds : [],
        layerRegistry
      ),
    })
  ), [isSourceImageMode, hasTimelineMedia, includeAnnotations, layerRegistry, workspace?.layerIds]);
  const videoTimelineBounds = useMemo(() => {
    if (!isVideoExportMode) return null;
    if (isSourceVideoMode) {
      const maxMs = Math.max(100, Math.round(sourceVideoMeta?.durationMs ?? 1000));
      return { minMs: 0, maxMs };
    }
    const workspaceDurationMs = Math.max(0, Math.round(toFiniteTimelineMs(workspace?.timelineDurationMs) ?? 0));
    const minStartMs = visibleTimelineTracks.reduce((min, track) => Math.min(min, Math.max(0, track.startMs)), Number.POSITIVE_INFINITY);
    const explicitOrFallbackMaxEndMs = visibleTimelineTracks.reduce((max, track) => {
      const candidateEndMs =
        typeof track.endMs === 'number' && Number.isFinite(track.endMs)
          ? track.endMs
          : (workspaceDurationMs > track.startMs ? workspaceDurationMs : (track.startMs + 1000));
      return Math.max(max, candidateEndMs);
    }, 0);
    const minMs = Number.isFinite(minStartMs) ? Math.max(0, Math.round(minStartMs)) : 0;
    const maxCandidateMs = Math.max(explicitOrFallbackMaxEndMs, minMs + 1000);
    const maxMs = Math.max(minMs + 100, Math.round(maxCandidateMs));
    return { minMs, maxMs };
  }, [isSourceVideoMode, isVideoExportMode, sourceVideoMeta?.durationMs, visibleTimelineTracks, workspace?.timelineDurationMs]);

  useEffect(() => {
    if (!isOpen || !workspace) return;
    const initializationKey = `${resolvedWorkspaceId}:${workspace.id}:${payload?.defaultTarget || 'area'}:${requestedExportMode}`;
    if (modalInitializationKeyRef.current === initializationKey) return;
    modalInitializationKeyRef.current = initializationKey;

    if (sourcePreviewUrlRef.current) {
      URL.revokeObjectURL(sourcePreviewUrlRef.current);
      sourcePreviewUrlRef.current = null;
    }

    const lastSettings = userPreferences.lastImageExportSettings;
    const resolvedTarget: ExportTarget =
      lastSettings?.target === 'frame' || lastSettings?.target === 'area'
        ? lastSettings.target
        : (payload?.defaultTarget || 'area');
    const resolvedFormat: ImageExportFileFormat =
      isSourceImageMode
        ? 'png'
        : isImageExportFileFormat(lastSettings?.format)
          ? lastSettings.format
          : 'png';
    const resolvedMegapixels =
      typeof lastSettings?.megapixels === 'number' && Number.isFinite(lastSettings.megapixels) && lastSettings.megapixels > 0
        ? lastSettings.megapixels
        : 6;
    const resolvedQuality =
      typeof lastSettings?.qualityPercent === 'number' && Number.isFinite(lastSettings.qualityPercent)
        ? clamp(lastSettings.qualityPercent, MIN_QUALITY_PERCENT, 100)
        : DEFAULT_QUALITY_PERCENT;
    const persistedPrint = normalizePrintPreferences(lastSettings?.print);
    const resolvedPrint: PrintExportOptions = { ...defaultPrintOptions(resolvedTarget), ...(persistedPrint ?? {}) };

    setTarget(resolvedTarget);
    setFormat(resolvedFormat);
    setIsPrintMode(false);
    setPrintTarget(resolvedPrint.target);
    setPrintOptions(resolvedPrint);
    setPrintProgressPercent(0);
    setPrintError(null);
    setMegapixels(resolvedMegapixels);
    setNumericDraft(null);
    setNumericNote(null);
    setQualityPercent(resolvedQuality);
    setPreserveAlpha(true);
    setIncludeAnnotations(false);
    setSourcePreviewUrl(null);
    setSourcePreviewError(null);
    setSourcePreviewTargetSize(null);
    setVideoOutputWidth(null);
    setVideoOutputHeight(null);
    const resolvedVideoFps =
      typeof workspace.timelineFps === 'number' && Number.isFinite(workspace.timelineFps)
        ? Math.max(8, Math.min(60, Math.round(workspace.timelineFps)))
        : 30;
    setVideoOutputFps(resolvedVideoFps);
    setVideoOutputFormat('mp4');
    setVideoQualityPreset(DEFAULT_VIDEO_QUALITY_PRESET);
    setBackendProgressPercent(0);
    setVideoRangeStartMs(null);
    setVideoRangeEndMs(null);
    setVideoRangeTillEnd(true);
    setVideoTracksPulled(0);
    setVideoTracksTotal(0);
    setVideoProcessingStep('analyzing');
    setExportSoundEnabled(true);
  }, [
    isOpen,
    payload?.defaultTarget,
    requestedExportMode,
    resolvedWorkspaceId,
    userPreferences.lastImageExportSettings,
    workspace?.id,
    workspace?.timelineFps,
  ]);

  useEffect(() => {
    if (isOpen) return;
    modalInitializationKeyRef.current = null;
  }, [isOpen]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      timelineExportAbortControllerRef.current?.abort();
      timelineExportAbortControllerRef.current = null;
      printExportAbortControllerRef.current?.abort();
      printExportAbortControllerRef.current = null;
      if (sourcePreviewUrlRef.current) {
        URL.revokeObjectURL(sourcePreviewUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) return;
    timelineExportAbortControllerRef.current?.abort();
    timelineExportAbortControllerRef.current = null;
    printExportAbortControllerRef.current?.abort();
    printExportAbortControllerRef.current = null;
    timelineExportInFlightRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !videoTimelineBounds) return;
    const minMs = videoTimelineBounds.minMs;
    const maxMs = videoTimelineBounds.maxMs;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Clamp the draft range to the resolved video bounds.
    setVideoRangeStartMs((current) => {
      if (typeof current !== 'number' || !Number.isFinite(current)) return minMs;
      return clamp(Math.round(current), minMs, Math.max(minMs, maxMs - 100));
    });
    setVideoRangeEndMs((current) => {
      if (videoRangeTillEnd) return null;
      if (typeof current !== 'number' || !Number.isFinite(current)) return maxMs;
      return clamp(Math.round(current), Math.min(maxMs, minMs + 100), maxMs);
    });
  }, [isOpen, videoRangeTillEnd, videoTimelineBounds]);

  const effectiveVideoRange = useMemo(() => {
    if (!videoTimelineBounds) return null;
    const minMs = Math.max(0, Math.round(videoTimelineBounds.minMs));
    const maxMs = Math.max(minMs + 100, Math.round(videoTimelineBounds.maxMs));
    const startBase = typeof videoRangeStartMs === 'number' && Number.isFinite(videoRangeStartMs)
      ? Math.round(videoRangeStartMs)
      : minMs;
    const clampedStart = clamp(startBase, minMs, Math.max(minMs, maxMs - 100));
    const hasManualEnd = !videoRangeTillEnd && typeof videoRangeEndMs === 'number' && Number.isFinite(videoRangeEndMs);
    const endBase = hasManualEnd
      ? Math.round(videoRangeEndMs)
      : maxMs;
    const clampedEnd = clamp(endBase, clampedStart + 100, maxMs);
    const startMs = Math.min(clampedStart, clampedEnd - 100);
    const endMs = Math.max(startMs + 100, clampedEnd);
    return { startMs, endMs, endMsForRequest: videoRangeTillEnd ? null : endMs };
  }, [videoRangeEndMs, videoRangeStartMs, videoRangeTillEnd, videoTimelineBounds]);

  const effectiveMegapixels = useMemo(
    () => (Number.isFinite(megapixels) && megapixels > 0 ? megapixels : MIN_EXPORT_MEGAPIXELS),
    [megapixels]
  );
  const effectiveQuality = useMemo(() => clamp(qualityPercent, MIN_QUALITY_PERCENT, 100), [qualityPercent]);
  const effectiveAlpha = format === 'jpeg' || format === 'pdf' ? false : preserveAlpha;

  const referenceRect: Rect | null = useMemo(() => {
    // Source-image mode: the asset's own dimensions drive the whole size
    // pipeline (aspect, megapixels, width/height controls).
    if (isSourceImageMode) {
      return sourceImageDims ? { x: 0, y: 0, w: sourceImageDims.w, h: sourceImageDims.h } : null;
    }
    if (!workspace) return null;
    return target === 'frame' ? workspace.frameRect : workspace.generationRect;
  }, [isSourceImageMode, sourceImageDims, workspace, target]);

  const referenceAspectRatio = useMemo(() => {
    if (!referenceRect || referenceRect.w <= 0 || referenceRect.h <= 0) return null;
    return referenceRect.w / referenceRect.h;
  }, [referenceRect]);

  // Megapixels is the only stored web size: width and height derive from it, so nothing has to
  // re-sync them when the reference rect changes.
  const imageAspectRatio = referenceAspectRatio ?? DEFAULT_IMAGE_ASPECT_RATIO;
  const imageSize = useMemo(
    () => resolveImageExportSize('megapixels', effectiveMegapixels, imageAspectRatio, MIN_EXPORT_SIDE, MAX_EXPORT_SIDE),
    [effectiveMegapixels, imageAspectRatio]
  );

  // Layer rects are frame-local. A layer with neither a decoded bitmap nor a scale is not measurable
  // and is left out, rather than given the heatmap's display default of 1.
  const optimalExport = useMemo(() => {
    if (isSourceImageMode) {
      return sourceImageDims ? { megapixels: (sourceImageDims.w * sourceImageDims.h) / 1_000_000, reason: 'native' as const } : null;
    }
    if (!referenceRect || !workspaceFrameRect) return null;
    const layers = collectVisibleImageExportLayers(Array.isArray(workspace?.layerIds) ? workspace.layerIds : [], layerRegistry)
      .filter((layer) => !isLayerVectorized(layer) && !getDrawingLayerSvgSource(layer.params))
      .map((layer) => {
        const worldPerPixel = resolveLayerWorldPerPixel(layer.rect.w, layer.imageData?.width, layer.scale);
        return { density: worldPerPixel ? 1 / worldPerPixel : 0, rect: layer.rect };
      });
    return resolveOptimalExportMegapixels(layers, {
      x: referenceRect.x - workspaceFrameRect.x,
      y: referenceRect.y - workspaceFrameRect.y,
      w: referenceRect.w,
      h: referenceRect.h,
    });
  }, [isSourceImageMode, layerRegistry, referenceRect, sourceImageDims, workspace?.layerIds, workspaceFrameRect]);

  const effectivePrintOptions = useMemo<PrintExportOptions>(() => ({ ...printOptions, target: printTarget }), [printOptions, printTarget]);
  const printDimensions = useMemo(() => resolvePrintDimensions(effectivePrintOptions), [effectivePrintOptions]);
  const roundedExportWidth = useMemo(() => isPrintMode ? printDimensions.width : clamp(Math.round(imageSize?.width ?? MIN_EXPORT_SIDE), MIN_EXPORT_SIDE, MAX_EXPORT_SIDE), [imageSize, isPrintMode, printDimensions.width]);
  const roundedExportHeight = useMemo(() => isPrintMode ? printDimensions.height : clamp(Math.round(imageSize?.height ?? MIN_EXPORT_SIDE), MIN_EXPORT_SIDE, MAX_EXPORT_SIDE), [imageSize, isPrintMode, printDimensions.height]);

  const exportPixelCount = useMemo(() => {
    return Math.max(1, roundedExportWidth * roundedExportHeight);
  }, [roundedExportWidth, roundedExportHeight]);

  const estimatedExportBytes = useMemo(() => {
    return estimateBytesFromSettings({
      pixelCount: exportPixelCount,
      format: isPrintMode ? effectivePrintOptions.format : format,
      qualityPercent: effectiveQuality,
      alpha: effectiveAlpha,
      annotations: includeAnnotations,
    });
  }, [
    exportPixelCount,
    format,
    effectivePrintOptions.format,
    isPrintMode,
    effectiveQuality,
    effectiveAlpha,
    includeAnnotations,
  ]);

  const exportExtension = isVideoExportMode
    ? (isSourceVideoMode ? 'mp4' : videoOutputFormat)
    : (isPrintMode ? (effectivePrintOptions.format === 'jpeg' ? 'jpg' : 'tif') : (format === 'jpeg' ? 'jpg' : format));
  const exportBaseName = useMemo(() => {
    const safeProject = (projectName || 'project').trim() || 'project';
    if (isSourceImageMode) {
      const safeLabel = (payload?.sourceLabel || 'output').trim().slice(0, 60) || 'output';
      return `${safeProject}-${safeLabel}`;
    }
    const safeWorkspace = (workspace?.name || workspace?.id || 'workspace').trim() || 'workspace';
    return `${safeProject}-${safeWorkspace}`;
  }, [projectName, workspace?.id, workspace?.name, isSourceImageMode, payload?.sourceLabel]);
  const effectiveExportBaseName = (customExportName || '').trim() || exportBaseName;
  const downloadFileName = `${safeFileName(effectiveExportBaseName)}.${exportExtension}`;

  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset the filename when opening a different export target.
    setCustomExportName(exportBaseName);
  }, [exportBaseName, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (format === 'svg' && !nativeSvgExportAvailable) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- SVG is invalid for source-image exports and mixed raster canvases.
      setFormat('png');
    }
  }, [format, isOpen, nativeSvgExportAvailable]);

  const renderWithSettings = useCallback(async (targetPixels: number) => {
    if (!kernel.current) {
      throw new Error('Workspace not available for export.');
    }
    return kernel.current.renderImageExport({
      workspaceId,
      target,
      format: isPrintMode ? (effectivePrintOptions.format === 'jpeg' ? 'jpeg' : 'png') : format,
      targetPixels,
      exportWidth: roundedExportWidth,
      exportHeight: roundedExportHeight,
      qualityPercent: effectiveQuality,
      alpha: effectiveAlpha,
      includeAnnotations,
      sourceImageUrl,
      noVisibleLayersMessage: t.noVisibleLayers,
      print: isPrintMode ? effectivePrintOptions : undefined,
      printSignal: printExportAbortControllerRef.current?.signal,
      onPrintProgress: isPrintMode ? setPrintProgressPercent : undefined,
    });
  }, [
    kernel,
    workspaceId,
    target,
    format,
    roundedExportWidth,
    roundedExportHeight,
    effectiveQuality,
    effectiveAlpha,
    includeAnnotations,
    sourceImageUrl,
    t.noVisibleLayers,
    isPrintMode,
    effectivePrintOptions,
  ]);

  const renderSourcePreviewOnce = useCallback(async () => {
    if (!kernel.current) {
      throw new Error('Workspace not available for export preview.');
    }
    return kernel.current.renderImageExport({
      workspaceId,
      target,
      format: 'webp',
      targetPixels: SOURCE_PREVIEW_TARGET_PIXELS,
      exportWidth: roundedExportWidth,
      exportHeight: roundedExportHeight,
      qualityPercent: 92,
      alpha: true,
      includeAnnotations: false,
      sourceImageUrl,
    });
  }, [kernel, roundedExportHeight, roundedExportWidth, sourceImageUrl, target, workspaceId]);

  useEffect(() => {
    if (!isOpen || isPrintMode || !isSourceImageMode || isVideoExportMode) return;

    let isActive = true;
    const timeoutId = window.setTimeout(() => {
      const requestId = sourcePreviewRequestIdRef.current + 1;
      sourcePreviewRequestIdRef.current = requestId;
      setIsSourcePreviewLoading(true);
      setSourcePreviewError(null);

      const run = async () => {
        try {
          const result = await renderSourcePreviewOnce();
          if (!isActive || requestId !== sourcePreviewRequestIdRef.current) return;

          if (!result.file) {
            throw new Error('Could not generate preview file.');
          }

          if (sourcePreviewUrlRef.current) {
            URL.revokeObjectURL(sourcePreviewUrlRef.current);
          }

          const nextPreviewUrl = URL.createObjectURL(result.file);
          sourcePreviewUrlRef.current = nextPreviewUrl;
          setSourcePreviewUrl(nextPreviewUrl);
          setSourcePreviewTargetSize({
            width: result.targetWidth,
            height: result.targetHeight,
          });
        } catch (error: any) {
          if (!isActive || requestId !== sourcePreviewRequestIdRef.current) return;
          setSourcePreviewUrl(null);
          setSourcePreviewTargetSize(null);
          setSourcePreviewError(error?.message || 'Failed to generate preview.');
        } finally {
          if (isActive && requestId === sourcePreviewRequestIdRef.current) {
            setIsSourcePreviewLoading(false);
          }
        }
      };

      void run();
    }, 2000);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
      setIsSourcePreviewLoading(false);
    };
  }, [isPrintMode, isSourceImageMode, isVideoExportMode, isOpen, renderSourcePreviewOnce]);

  useEffect(() => {
    if (!isOpen || isVideoExportMode || isPrintMode) return;
    const viewport = previewViewportRef.current;
    if (!viewport) return;

    const updateSize = () => {
      const rect = viewport.getBoundingClientRect();
      setPreviewViewportSize({ width: rect.width, height: rect.height });
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [isOpen, isPrintMode, isVideoExportMode]);

  const previewTarget = isPrintMode ? printTarget : target;
  const previewTargetRect = isSourceImageMode
    ? null
    : (previewTarget === 'frame' ? workspaceFrameRect : workspaceGenerationRect);
  const previewFit = resolveExportPreviewFit(previewTargetRect, previewViewportSize.width, previewViewportSize.height);
  const previewFrameRect = workspaceFrameRect && previewTargetRect
    ? {
        ...workspaceFrameRect,
        x: workspaceFrameRect.x - previewTargetRect.x,
        y: workspaceFrameRect.y - previewTargetRect.y,
      }
    : null;
  const previewFrameBackgroundColor = workspace?.backgroundColor ?? (
    (workspace?.layerIds?.length ?? 0) === 0 ? 'transparent' : '#000000'
  );
  const previewResizeHandleMouseDown = useCallback(() => {}, []);
  const blockPreviewInteraction = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const defaultVideoOutput = useMemo(() => {
    // The composition frame in *world units*. This only fixes the output shape
    // (aspect ratio + layout) — never the pixel budget. Prefer the union bounding
    // box of the visible visual tracks; fall back to the workspace frame.
    const visualTracks = stableVideoTrackPayload.filter(
      (track) => track.layerType === 'video' || track.layerType === 'image'
    );
    let frameWidth: number | null = null;
    let frameHeight: number | null = null;
    if (isSourceVideoMode && sourceVideoMeta) {
      return resolveVideoExportSizeFromWidth(sourceVideoMeta.width, sourceVideoMeta.width / Math.max(1, sourceVideoMeta.height));
    }
    if (visualTracks.length > 0) {
      const bounds = visualTracks.reduce(
        (acc, track) => {
          const drawW = Number.isFinite(track.rect.w) && track.rect.w > 0
            ? track.rect.w
            : 0;
          const drawH = Number.isFinite(track.rect.h) && track.rect.h > 0
            ? track.rect.h
            : 0;
          const x = Number.isFinite(track.rect.x) ? track.rect.x : 0;
          const y = Number.isFinite(track.rect.y) ? track.rect.y : 0;
          return {
            minX: Math.min(acc.minX, x),
            minY: Math.min(acc.minY, y),
            maxX: Math.max(acc.maxX, x + drawW),
            maxY: Math.max(acc.maxY, y + drawH),
          };
        },
        {
          minX: Number.POSITIVE_INFINITY,
          minY: Number.POSITIVE_INFINITY,
          maxX: Number.NEGATIVE_INFINITY,
          maxY: Number.NEGATIVE_INFINITY,
        }
      );
      const trackWidth = bounds.maxX - bounds.minX;
      const trackHeight = bounds.maxY - bounds.minY;
      if (
        Number.isFinite(trackWidth) &&
        Number.isFinite(trackHeight) &&
        trackWidth > 1 &&
        trackHeight > 1
      ) {
        frameWidth = trackWidth;
        frameHeight = trackHeight;
      }
    }
    if (frameWidth === null || frameHeight === null) {
      const fallbackWidth = Number(workspaceFrameRect?.w);
      const fallbackHeight = Number(workspaceFrameRect?.h);
      if (
        Number.isFinite(fallbackWidth) &&
        Number.isFinite(fallbackHeight) &&
        fallbackWidth > 1 &&
        fallbackHeight > 1
      ) {
        frameWidth = fallbackWidth;
        frameHeight = fallbackHeight;
      }
    }
    if (frameWidth === null || frameHeight === null) return null;

    // Pixel budget = the finest native resolution any *visible video* layer can
    // actually supply. A layer's rect (world units) equals its native pixels × scale,
    // so native pixels-per-world-unit density is 1 / scale. Rendering the frame at the
    // sharpest video's density lets that source reach its native resolution without
    // wasting pixels upscaling — which is exactly what a frameRect-based (display-size)
    // default did when it proposed 4K for content that could never fill it.
    const videoDensities = stableVideoTrackPayload
      .filter((track) => track.layerType === 'video')
      .map((track) => (Number.isFinite(track.scale) && track.scale > 0 ? 1 / track.scale : null))
      .filter((density): density is number => density !== null && Number.isFinite(density) && density > 0);
    const density = videoDensities.length > 0 ? Math.max(...videoDensities) : 1;

    const aspectRatio = frameWidth / frameHeight;
    return resolveVideoExportSizeFromWidth(frameWidth * density, aspectRatio);
  }, [isSourceVideoMode, sourceVideoMeta, stableVideoTrackPayload, workspaceFrameRect?.h, workspaceFrameRect?.w]);

  const fullVideoOutputWidth = videoOutputWidth ?? defaultVideoOutput?.width ?? null;
  const fullVideoOutputHeight = videoOutputHeight ?? defaultVideoOutput?.height ?? null;
  const videoOutputAspectRatio = useMemo(() => {
    if (defaultVideoOutput && defaultVideoOutput.width > 0 && defaultVideoOutput.height > 0) {
      return defaultVideoOutput.width / defaultVideoOutput.height;
    }
    if (
      fullVideoOutputWidth !== null &&
      fullVideoOutputHeight !== null &&
      fullVideoOutputWidth > 0 &&
      fullVideoOutputHeight > 0
    ) {
      return fullVideoOutputWidth / fullVideoOutputHeight;
    }
    return null;
  }, [defaultVideoOutput, fullVideoOutputHeight, fullVideoOutputWidth]);

  useEffect(() => {
    if (!isOpen || !isVideoExportMode) return;
    if (!defaultVideoOutput) return;
    if (videoOutputWidth === null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Seed unset video export dimensions from the source media.
      setVideoOutputWidth(defaultVideoOutput.width);
    }
    if (videoOutputHeight === null) {
      setVideoOutputHeight(defaultVideoOutput.height);
    }
  }, [defaultVideoOutput, isVideoExportMode, isOpen, videoOutputHeight, videoOutputWidth]);

  const displayedVideoBytes = useMemo(() => {
    if (!isVideoExportMode) return estimatedExportBytes;
    if (
      fullVideoOutputWidth === null ||
      fullVideoOutputHeight === null ||
      !effectiveVideoRange
    ) {
      return null;
    }
    const durationSec = Math.max(0.1, (effectiveVideoRange.endMs - effectiveVideoRange.startMs) / 1000);
    const fps = Math.max(8, Math.min(60, Math.round(videoOutputFps)));
    const pixels = Math.max(1, fullVideoOutputWidth * fullVideoOutputHeight);
    const qualityMultiplier =
      videoQualityPreset === 'draft'
        ? 0.8
        : (videoQualityPreset === 'high' ? 1.35 : 1);
    const videoBitrate = clamp(
      Math.round(pixels * fps * 0.08 * qualityMultiplier),
      900_000,
      28_000_000
    );
    const audioBitrate = exportSoundEnabled
      ? (videoQualityPreset === 'draft' ? 128_000 : (videoQualityPreset === 'high' ? 192_000 : 160_000))
      : 0;
    return Math.max(1, Math.round((durationSec * (videoBitrate + audioBitrate)) / 8));
  }, [
    effectiveVideoRange,
    estimatedExportBytes,
    exportSoundEnabled,
    isSourceVideoMode,
    isVideoExportMode,
    fullVideoOutputHeight,
    fullVideoOutputWidth,
    videoOutputFps,
    videoQualityPreset,
  ]);

  // Kernel status feed -> local display state. The composition itself lives in
  // kernel.composeWorkspaceTimelineVideo.
  const applyBackendStatus = useCallback((statusPayload: TimelineCompositionStatus) => {
    if (!statusPayload) return;
    const progressPercent = Number.isFinite(statusPayload.percent) ? Number(statusPayload.percent) : 0;
    setBackendProgressPercent(clamp(Math.round(progressPercent), 0, 100));
    if (Number.isFinite(statusPayload.totalTracks) && statusPayload.totalTracks > 0) {
      setVideoTracksPulled(Math.max(0, Math.min(statusPayload.totalTracks, statusPayload.tracksPulled || 0)));
      setVideoTracksTotal(Math.max(1, Math.round(statusPayload.totalTracks)));
    }
    let nextStep: VideoProcessingStep = 'analyzing';
    if (statusPayload.phase === 'pulling') {
      nextStep = 'pulling';
    } else if (statusPayload.phase === 'building') {
      nextStep = 'building';
    } else if (statusPayload.phase === 'finalizing') {
      nextStep = 'finalizing';
    } else if (statusPayload.phase === 'ready') {
      nextStep = 'done';
    }
    const stepRank: Record<VideoProcessingStep, number> = {
      analyzing: 0,
      pulling: 1,
      building: 2,
      finalizing: 3,
      done: 4,
    };
    setVideoProcessingStep((currentStep) => (
      stepRank[nextStep] >= stepRank[currentStep] ? nextStep : currentStep
    ));
  }, []);

  const handleDownload = useCallback(async () => {
    if (!kernel.current) return;
    if (!workspace && !isSourceImageMode && !isSourceVideoMode) return;
    if (timelineExportInFlightRef.current) return;
    timelineExportInFlightRef.current = true;

    let downloadAbortController: AbortController | null = (isVideoExportMode || isPrintMode) ? new AbortController() : null;
    let downloadTriggered = false;
    timelineExportAbortControllerRef.current = isVideoExportMode ? downloadAbortController : null;
    printExportAbortControllerRef.current = isPrintMode ? downloadAbortController : null;
    try {
      if (isPrintMode) {
        const validationError = validatePrintExportOptions(effectivePrintOptions);
        if (validationError) throw new Error(validationError);
        setPrintError(null);
        setPrintProgressPercent(0);
      }
      let finalFileName = isVideoExportMode
        ? `${safeFileName(effectiveExportBaseName)}.${isSourceVideoMode ? 'mp4' : videoOutputFormat}`
        : downloadFileName;
      const saveTarget = await requestExportSaveTarget(finalFileName, exportExtension);
      if (!saveTarget || downloadAbortController?.signal.aborted) {
        return;
      }

      setIsDownloading(true);
      setBackendProgressPercent(0);
      setVideoProcessingStep('analyzing');
      setVideoTracksPulled(0);
      setVideoTracksTotal(Math.max(1, stableVideoTrackPayload.length));

      let fileToDownload: Blob | null = null;
      if (isVideoExportMode) {
        if (isSourceVideoMode) {
          if (!sourceVideoUrl) throw new Error('Video export is not ready.');
          const response = await fetch(sourceVideoUrl, { signal: downloadAbortController?.signal });
          if (!response.ok) throw new Error('Video export failed.');
          fileToDownload = await response.blob();
        } else {
          if (!effectiveVideoRange) {
            throw new Error('Video export is not ready.');
          }
          const videoBlob = (await kernel.current.composeWorkspaceTimelineVideo({
          workspaceId: workspace?.id ?? workspaceId ?? '',
          startMs: effectiveVideoRange.startMs,
          endMs: effectiveVideoRange.endMsForRequest ?? undefined,
          outputWidth: fullVideoOutputWidth ?? 1280,
          outputHeight: fullVideoOutputHeight ?? 720,
          fps: videoOutputFps,
          outputFormat: videoOutputFormat,
          qualityPreset: videoQualityPreset,
          includeAudio: exportSoundEnabled,
          signal: downloadAbortController?.signal,
          tracks: stableVideoTrackPayload,
          onStatus: applyBackendStatus,
          })).blob;
          const safeBaseName = safeFileName(effectiveExportBaseName);
          finalFileName = `${safeBaseName}.${videoOutputFormat}`;
          fileToDownload = videoBlob;
        }
      } else {
        const result = await renderWithSettings(exportPixelCount);
        fileToDownload = result.file ? await signImageExport(result.file) : null;
      }

      if (!fileToDownload) {
        throw new Error(isVideoExportMode ? 'No video produced for export.' : 'No file produced for export.');
      }

      const savedFileName = await saveExportBlob(fileToDownload, finalFileName, saveTarget);
      downloadTriggered = true;

      if (isVideoExportMode) {
        kernel.current.logUserAction(isSourceVideoMode ? 'List Video Output Exported via Modal' : 'Inpaint Video Exported via Modal', {
          workspaceId: workspace?.id ?? workspaceId,
          timeline_start_ms: effectiveVideoRange?.startMs ?? null,
          timeline_end_ms: effectiveVideoRange?.endMsForRequest ?? null,
          timeline_till_end: effectiveVideoRange?.endMsForRequest === null,
          output_format: videoOutputFormat,
          quality_preset: videoQualityPreset,
          include_audio: exportSoundEnabled,
          file_name: savedFileName,
        });
      } else {
        kernel.current.logUserAction(isPrintMode ? 'Print Grand Format Exported via Modal' : 'Inpaint Image Exported via Modal', isPrintMode ? {
          workspaceId: workspace?.id ?? workspaceId ?? '',
          target: printTarget,
          print_format: effectivePrintOptions.format,
          width_cm: effectivePrintOptions.widthCm,
          height_cm: effectivePrintOptions.heightCm,
          dpi: effectivePrintOptions.dpi,
          bleed_mm: effectivePrintOptions.bleedMm,
          icc_profile: effectivePrintOptions.iccProfile,
          file_name: savedFileName,
        } : {
          workspaceId,
          target,
          format,
          megapixels: effectiveMegapixels,
          quality_percent: effectiveQuality,
          alpha: effectiveAlpha,
          annotations: includeAnnotations,
          file_name: savedFileName,
        });

        try {
          await kernel.current.updateAndSaveUserPreference('lastImageExportSettings', isPrintMode
            ? {
                ...userPreferences.lastImageExportSettings,
                print: effectivePrintOptions,
              }
            : {
                ...userPreferences.lastImageExportSettings,
                format,
                target,
                megapixels: effectiveMegapixels,
                qualityPercent: effectiveQuality,
              });
        } catch (preferenceError) {
          console.warn('[ImageExportModal] Failed to persist export settings:', preferenceError);
        }
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        if (isPrintMode && isMountedRef.current) setPrintError(error?.message || 'Print export failed.');
        kernel.current.handleError(error?.message || (isVideoExportMode ? 'Video export failed.' : 'Image export failed.'));
      }
    } finally {
      if (timelineExportAbortControllerRef.current === downloadAbortController) {
        timelineExportAbortControllerRef.current = null;
      }
      if (printExportAbortControllerRef.current === downloadAbortController) {
        printExportAbortControllerRef.current = null;
      }
      timelineExportInFlightRef.current = false;
      if (isMountedRef.current) {
        setIsDownloading(false);
        if (downloadTriggered) {
          onClose();
        }
      }
    }
  }, [
    kernel,
    workspace,
    isSourceImageMode,
    isSourceVideoMode,
    renderWithSettings,
    sourceVideoUrl,
    exportPixelCount,
    exportExtension,
    downloadFileName,
    target,
    format,
    isPrintMode,
    printTarget,
    effectivePrintOptions,
    userPreferences.lastImageExportSettings,
    effectiveMegapixels,
    effectiveQuality,
    effectiveAlpha,
    includeAnnotations,
    exportSoundEnabled,
    onClose,
    effectiveExportBaseName,
    fullVideoOutputWidth,
    fullVideoOutputHeight,
    videoOutputFps,
    applyBackendStatus,
    effectiveVideoRange,
    isVideoExportMode,
    stableVideoTrackPayload,
    videoOutputFormat,
    videoQualityPreset,
  ]);

  const handleSendToNewProject = useCallback(async () => {
    if (!kernel.current) return;
    if (!workspace && !isSourceImageMode) return;
    if (timelineExportInFlightRef.current) return;
    timelineExportInFlightRef.current = true;
    setIsDownloading(true);
    let file: File | null = null;
    try {
      file = (await renderWithSettings(exportPixelCount)).file ?? null;
      if (!file) throw new Error('No file produced for export.');
    } catch (error: any) {
      kernel.current.handleError(error?.message || 'Image export failed.');
    } finally {
      timelineExportInFlightRef.current = false;
      if (isMountedRef.current) setIsDownloading(false);
    }
    if (!file) return;
    void kernel.current.sendImageToNewProject(file, effectiveExportBaseName);
    onClose();
  }, [kernel, workspace, isSourceImageMode, renderWithSettings, exportPixelCount, effectiveExportBaseName, onClose]);

  const videoTrackPullTotal = useMemo(
    () => Math.max(1, videoTracksTotal, stableVideoTrackPayload.length),
    [stableVideoTrackPayload.length, videoTracksTotal]
  );
  const videoStepOrder = useMemo<VideoProcessingStep[]>(
    () => ['analyzing', 'pulling', 'building', 'finalizing', 'done'],
    []
  );
  const videoStepIndex = useMemo(
    () => Math.max(0, videoStepOrder.indexOf(videoProcessingStep)),
    [videoProcessingStep, videoStepOrder]
  );
  const isVideoProcessingOverlayVisible = isVideoExportMode && isDownloading;
  const isPrintProcessingOverlayVisible = isPrintMode && isDownloading;
  const videoRangeMinSec = (videoTimelineBounds?.minMs ?? 0) / 1000;
  const videoRangeMaxSec = (videoTimelineBounds?.maxMs ?? 0) / 1000;
  const selectedStartSec = effectiveVideoRange ? effectiveVideoRange.startMs / 1000 : 0;
  const selectedEndSec = effectiveVideoRange ? effectiveVideoRange.endMs / 1000 : 0;
  const downloadSizeLabel = isVideoExportMode
    ? (displayedVideoBytes !== null ? formatBytes(displayedVideoBytes) : '--')
    : formatBytes(estimatedExportBytes);

  const handleModalBackdropPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  }, [onClose]);

  const stopModalPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleExportNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomExportName(event.target.value);
  }, []);

  const handleVideoOutputFormatChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setVideoOutputFormat(event.target.value as VideoOutputFormat);
  }, []);

  const handleVideoQualityPresetChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setVideoQualityPreset(event.target.value as VideoQualityPreset);
  }, []);

  const handleNumericDraftChange = useCallback((field: NumericFieldKey, text: string | null) => {
    setNumericDraft(text === null ? null : { field, text });
    setNumericNote(null);
  }, []);

  const settleNumericDraft = useCallback((field: NumericFieldKey, note: string | null) => {
    setNumericDraft(null);
    setNumericNote(note ? { field, text: note } : null);
  }, []);

  const commitImageSizeField = useCallback((field: NumericFieldKey, text: string) => {
    if (field !== 'megapixels' && field !== 'width' && field !== 'height') return;
    const parsed = parseDraftNumber(text);
    const next = parsed === null ? null : resolveImageExportSize(field, parsed, imageAspectRatio, MIN_EXPORT_SIDE, MAX_EXPORT_SIDE);
    if (next) setMegapixels(next.megapixels);
    settleNumericDraft(field, formatBoundNote(next?.bound ?? null, next?.bound === 'max' ? MAX_EXPORT_SIDE : MIN_EXPORT_SIDE, 'px', t));
  }, [imageAspectRatio, settleNumericDraft, t]);

  const handleOptimalMegapixels = useCallback(() => {
    if (optimalExport) commitImageSizeField('megapixels', String(optimalExport.megapixels));
  }, [commitImageSizeField, optimalExport]);

  const optimalMegapixelsButton = useMemo(() => {
    const reasonLabel = !optimalExport
      ? t.optimalUnavailable
      : ({ native: t.optimalNative, base: t.optimalBase, patch: t.optimalPatch, capped: t.optimalCapped, sparse: t.optimalSparse })[optimalExport.reason];
    return (
      <button
        type="button"
        onClick={handleOptimalMegapixels}
        disabled={!optimalExport}
        title={reasonLabel}
        aria-label={`${t.optimal}: ${reasonLabel}`}
        className="h-4 rounded-full px-2 text-[11px] leading-4 text-white/70 transition-colors duration-150 ease-out hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        {t.optimal}
      </button>
    );
  }, [handleOptimalMegapixels, optimalExport, t]);

  const commitPrintSizeField = useCallback((field: NumericFieldKey, text: string) => {
    if (field !== 'printWidthCm' && field !== 'printHeightCm') return;
    const parsed = parseDraftNumber(text);
    if (parsed === null) {
      settleNumericDraft(field, null);
      return;
    }
    const { value, bound } = clampPrintCm(parsed);
    setPrintOptions((current) => ({ ...current, preset: 'custom', ...resolvePrintSizeDraft(current, field, value) }));
    settleNumericDraft(field, formatBoundNote(bound, MIN_PRINT_CM, 'cm', t));
  }, [settleNumericDraft, t]);

  const commitVideoSizeField = useCallback((field: NumericFieldKey, text: string) => {
    if (field !== 'videoWidth' && field !== 'videoHeight') return;
    const parsed = parseDraftNumber(text);
    const next = parsed !== null && parsed > 0 ? resolveVideoSizeDraft(field, parsed, videoOutputAspectRatio) : null;
    if (next) {
      setVideoOutputWidth(next.width);
      setVideoOutputHeight(next.height);
    }
    settleNumericDraft(field, formatBoundNote(next?.bound ?? null, next?.bound === 'max' ? MAX_VIDEO_EXPORT_SIDE : MIN_VIDEO_EXPORT_SIDE, 'px', t));
  }, [settleNumericDraft, t, videoOutputAspectRatio]);

  const commitVideoFpsField = useCallback((field: NumericFieldKey, text: string) => {
    const parsed = parseDraftNumber(text);
    if (parsed === null) {
      settleNumericDraft(field, null);
      return;
    }
    const { value, bound } = clampWithBound(Math.round(parsed), MIN_VIDEO_FPS, MAX_VIDEO_FPS);
    setVideoOutputFps(value);
    settleNumericDraft(field, formatBoundNote(bound, bound === 'max' ? MAX_VIDEO_FPS : MIN_VIDEO_FPS, 'fps', t));
  }, [settleNumericDraft, t]);

  const commitVideoRangeField = useCallback((field: NumericFieldKey, text: string) => {
    if (field === 'videoEnd' && text.trim().length === 0) {
      setVideoRangeEndMs(null);
      setVideoRangeTillEnd(true);
      settleNumericDraft(field, null);
      return;
    }
    const parsed = parseDraftNumber(text);
    if (parsed === null) {
      settleNumericDraft(field, null);
      return;
    }
    const isStart = field === 'videoStart';
    const minSec = isStart ? videoRangeMinSec : Math.min(videoRangeMaxSec, selectedStartSec + MIN_VIDEO_RANGE_SEC);
    const maxSec = isStart ? Math.max(videoRangeMinSec, selectedEndSec - MIN_VIDEO_RANGE_SEC) : videoRangeMaxSec;
    const { value, bound } = clampWithBound(parsed, minSec, maxSec);
    if (isStart) {
      setVideoRangeStartMs(Math.round(value * 1000));
    } else {
      setVideoRangeTillEnd(false);
      setVideoRangeEndMs(Math.round(value * 1000));
    }
    settleNumericDraft(field, formatBoundNote(bound, roundTo(bound === 'max' ? maxSec : minSec, 2), 's', t));
  }, [selectedEndSec, selectedStartSec, settleNumericDraft, t, videoRangeMaxSec, videoRangeMinSec]);

  // What a pending draft would commit to, shown in the linked fields until it is committed.
  const numericPreview = useMemo(() => {
    const fields: Partial<Record<NumericFieldKey, string>> = {};
    const parsed = numericDraft ? parseDraftNumber(numericDraft.text) : null;
    if (!numericDraft || parsed === null) return { fields, printDimensions: null };
    const { field } = numericDraft;
    if (field === 'megapixels' || field === 'width' || field === 'height') {
      const next = resolveImageExportSize(field, parsed, imageAspectRatio, MIN_EXPORT_SIDE, MAX_EXPORT_SIDE);
      if (next) {
        fields.megapixels = formatMegapixels(next.megapixels);
        fields.width = formatImageSide(next.width);
        fields.height = formatImageSide(next.height);
      }
    } else if (field === 'printWidthCm' || field === 'printHeightCm') {
      const size = resolvePrintSizeDraft(printOptions, field, clampPrintCm(parsed).value);
      fields.printWidthCm = formatCm(size.widthCm);
      fields.printHeightCm = formatCm(size.heightCm);
      return { fields, printDimensions: resolvePrintDimensions({ ...effectivePrintOptions, ...size }) };
    } else if ((field === 'videoWidth' || field === 'videoHeight') && parsed > 0) {
      const next = resolveVideoSizeDraft(field, parsed, videoOutputAspectRatio);
      fields.videoWidth = String(next.width);
      fields.videoHeight = String(next.height);
    }
    return { fields, printDimensions: null };
  }, [effectivePrintOptions, imageAspectRatio, numericDraft, printOptions, videoOutputAspectRatio]);
  const shownPrintDimensions = numericPreview.printDimensions ?? printDimensions;
  const draftOf = (field: NumericFieldKey) => (numericDraft?.field === field ? numericDraft.text : null);
  const noteOf = (field: NumericFieldKey) => (numericNote?.field === field ? numericNote.text : null);

  const handleTargetChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setTarget(event.target.value as ExportTarget);
  }, []);

  const handleFormatChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setFormat(event.target.value as ImageExportFileFormat);
  }, []);

  const handlePrintModeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextIsPrintMode = event.target.value === 'print';
    setIsPrintMode(nextIsPrintMode);
    if (nextIsPrintMode) {
      if (sourcePreviewUrlRef.current) URL.revokeObjectURL(sourcePreviewUrlRef.current);
      sourcePreviewUrlRef.current = null;
      setSourcePreviewUrl(null);
      setSourcePreviewTargetSize(null);
    }
  }, []);

  const handlePrintTargetChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setPrintTarget(event.target.value as ExportTarget);
  }, []);

  const updatePrintOptions = useCallback((patch: Partial<PrintExportOptions>) => {
    setPrintOptions((current) => ({ ...current, ...patch }));
  }, []);

  const handlePrintFormatChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const format = event.target.value as PrintExportOptions['format'];
    setPrintOptions((current) => format === 'jpeg' && PRINT_ICC_PROFILES[current.iccProfile].colorSpace === 'cmyk'
      ? current
      : { ...current, format });
  }, []);

  const handlePrintIccProfileChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const iccProfile = event.target.value as PrintExportOptions['iccProfile'];
    setPrintOptions((current) => ({
      ...current,
      iccProfile,
      format: PRINT_ICC_PROFILES[iccProfile].colorSpace === 'cmyk' ? 'tiff' : current.format,
    }));
  }, []);

  const handlePrintPresetChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const preset = event.target.value as PrintExportOptions['preset'];
    const presetValues = resolvePrintPreset(preset);
    updatePrintOptions({ preset, ...(presetValues ?? {}) });
  }, [updatePrintOptions]);

  const cancelPrintExport = useCallback(() => {
    printExportAbortControllerRef.current?.abort();
  }, []);

  if (!isOpen) return null;

  if (isVideoProcessingOverlayVisible) {
    return (
      <div
        className="image-export-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/35 backdrop-blur-[3px]"
        onPointerDown={handleModalBackdropPointerDown}
      >
        <div
          className="image-export-modal-panel w-full max-w-md rounded-xl border border-white/10 bg-[#101010] p-4 text-white shadow-2xl"
          onPointerDown={stopModalPointerDown}
        >
          <div className="mb-3 flex items-center justify-between text-sm font-medium text-white/90">
            <div className="flex items-center gap-2">
              <Loader2 size={15} className="animate-spin" />
              {t.processingTitle}
            </div>
            <span className="font-mono text-xs text-white/70">{Math.round(backendProgressPercent)}%</span>
          </div>
          <div className="space-y-2 text-xs text-white/70">
            {videoStepOrder.map((step, index) => {
              const isDone = index < videoStepIndex;
              const isActive = index === videoStepIndex;
              let label = '';
              if (step === 'analyzing') label = t.processingAnalyzing;
              if (step === 'pulling') label = `${t.processingPullingTracks} (${videoTracksPulled}/${videoTrackPullTotal})`;
              if (step === 'building') label = t.processingBuilding;
              if (step === 'finalizing') label = t.processingFinalizing;
              if (step === 'done') label = t.processingDone;
              return (
                <div
                  key={step}
                  className={`flex items-center gap-2 ${isActive ? 'text-white/95' : (isDone ? 'text-white/80' : 'text-white/50')}`}
                >
                  {isActive ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <span className="w-3 text-center">{isDone ? '✓' : '•'}</span>
                  )}
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (isPrintProcessingOverlayVisible) {
    return (
      <div className="image-export-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/35 backdrop-blur-[3px]">
        <div className="image-export-modal-panel w-full max-w-md rounded-xl border border-white/10 bg-[#101010] p-4 text-white shadow-2xl">
          <div className="mb-3 flex items-center justify-between text-sm font-medium text-white/90">
            <span className="flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Print export</span>
            <span className="font-mono text-xs text-white/70">{Math.round(printProgressPercent)}%</span>
          </div>
          <div className="mb-3 h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full bg-[#3B82F6] transition-[width]" style={{ width: `${printProgressPercent}%` }} /></div>
          {printError && <p className="mb-3 text-xs text-red-300">{printError}</p>}
          <button onClick={cancelPrintExport} className="w-full rounded-md border border-white/10 px-3 py-2 text-xs text-white/80 hover:bg-white/10">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="image-export-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-sm"
      onPointerDown={handleModalBackdropPointerDown}
    >
      <div
        className="image-export-modal-panel relative flex flex-col overflow-hidden rounded-[24px] border border-white/[0.06] bg-[#212121] text-white shadow-2xl"
        style={{
          height: isVideoExportMode ? 'auto' : '92vh',
          maxHeight: isVideoExportMode ? '90vh' : '92vh',
          width: isVideoExportMode ? '92vw' : '96vw',
          maxWidth: isVideoExportMode ? '42rem' : '1800px',
        }}
        onPointerDown={stopModalPointerDown}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-3 py-2.5">
          <div>
            <h2 className="text-lg font-semibold text-white/90">{isVideoExportMode ? t.titleVideo : t.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-md border border-white/10 p-1 text-white/70 hover:bg-white/10 hover:text-white"
            title={t.close}
          >
            <X size={16} />
          </button>
        </div>

        <div
          className="grid min-h-0 flex-1 grid-cols-1 gap-3 p-3 lg:grid-cols-[1.15fr_1fr]"
          style={isVideoExportMode ? { display: 'block' } : undefined}
        >
          {!isVideoExportMode && (
            <div className="flex min-h-0 flex-col rounded-xl border border-[#2A2A2A] bg-[#141414] p-2.5">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white/55">{t.preview}</span>
                  {!isSourceImageMode && hasReducedResolutionPreview && (
                    <span className="text-[10px] text-white/35">{t.lightPreview}</span>
                  )}
                </div>
                {isSourceImageMode && sourcePreviewTargetSize && (
                  <span className="font-mono text-xs text-white/60">
                    {sourcePreviewTargetSize.width} x {sourcePreviewTargetSize.height}
                  </span>
                )}
              </div>
              <div
                ref={previewViewportRef}
                className="relative flex min-h-[220px] flex-1 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-black/30"
              >
                {isSourceImageMode ? (
                  <>
                    {isSourcePreviewLoading && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/45">
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <Loader2 size={14} className="animate-spin" />
                          {t.preparing}
                        </div>
                      </div>
                    )}
                    {sourcePreviewUrl ? (
                      <img
                        src={sourcePreviewUrl}
                        alt="Export preview"
                        className="max-h-full max-w-full object-contain"
                        draggable={false}
                      />
                    ) : (
                      <span className="px-4 text-center text-xs text-white/50">
                        {sourcePreviewError || t.noPreview}
                      </span>
                    )}
                  </>
                ) : previewFit && workspace && previewFrameRect ? (
                  <div
                    aria-label="Live workspace export preview"
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      width: previewFit.width,
                      height: previewFit.height,
                      overflow: 'hidden',
                      backgroundColor: 'transparent',
                      transform: `translate(-50%, -50%) scale(${previewFit.scale})`,
                      transformOrigin: 'center center',
                      pointerEvents: 'none',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: previewFrameRect.x,
                        top: previewFrameRect.y,
                        width: previewFrameRect.w,
                        height: previewFrameRect.h,
                        overflow: 'hidden',
                        backgroundColor: previewFrameBackgroundColor,
                      }}
                    >
                      <div
                        style={{ position: 'absolute', inset: 0, pointerEvents: 'auto' }}
                        onPointerDownCapture={blockPreviewInteraction}
                        onPointerMoveCapture={blockPreviewInteraction}
                        onPointerUpCapture={blockPreviewInteraction}
                        onTouchStartCapture={blockPreviewInteraction}
                        onTouchMoveCapture={blockPreviewInteraction}
                        onTouchEndCapture={blockPreviewInteraction}
                        onMouseDownCapture={blockPreviewInteraction}
                        onClickCapture={blockPreviewInteraction}
                        onDoubleClickCapture={blockPreviewInteraction}
                        onAuxClickCapture={blockPreviewInteraction}
                        onDragStartCapture={blockPreviewInteraction}
                        onWheelCapture={blockPreviewInteraction}
                        onContextMenuCapture={blockPreviewInteraction}
                      >
                        {visibleWorkspaceLayerIds.map((layerId) => (
                          <LayerCanvas
                            key={layerId}
                            layerId={layerId}
                            isWorkspaceVisible={true}
                            handleLayerMouseDown={null}
                            isWorkspaceLocked={true}
                            workspaceId={workspace.id}
                            preview
                            handleResizeHandleMouseDown={previewResizeHandleMouseDown}
                            resizeHandlesPortalHostRef={previewResizeHandlesPortalHostRef}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <span className="px-4 text-center text-xs text-white/50">
                    {t.noPreview}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2.5 overflow-y-auto rounded-xl border border-[#2A2A2A] bg-[#141414] p-2.5">
            <label className="block text-xs text-white/70">
              {t.exportName}
              <input
                type="text"
                value={customExportName}
                onChange={handleExportNameChange}
                placeholder={exportBaseName}
                className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]"
              />
            </label>

            {isVideoExportMode ? (
              isSourceVideoMode ? (
                <label className="block text-xs text-white/70">
                  {t.videoOutputFormat}
                  <select
                    value="mp4"
                    disabled
                    className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white/60 outline-none"
                  >
                    <option value="mp4">MP4</option>
                  </select>
                </label>
              ) : (
                <>
                  {videoTimelineBounds && effectiveVideoRange && (
                    <>
                    <div className="grid grid-cols-2 gap-2">
                      <DraftNumberField
                        field="videoWidth"
                        label={t.width}
                        value={fullVideoOutputWidth === null ? '' : String(fullVideoOutputWidth)}
                        draft={draftOf('videoWidth')}
                        preview={numericPreview.fields.videoWidth}
                        note={noteOf('videoWidth')}
                        onDraftChange={handleNumericDraftChange}
                        onCommit={commitVideoSizeField}
                      />
                      <DraftNumberField
                        field="videoHeight"
                        label={t.height}
                        value={fullVideoOutputHeight === null ? '' : String(fullVideoOutputHeight)}
                        draft={draftOf('videoHeight')}
                        preview={numericPreview.fields.videoHeight}
                        note={noteOf('videoHeight')}
                        onDraftChange={handleNumericDraftChange}
                        onCommit={commitVideoSizeField}
                      />
                      <label className="block text-xs text-white/70">
                        {t.videoOutputFormat}
                        <select
                          value={videoOutputFormat}
                          onChange={handleVideoOutputFormatChange}
                          className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]"
                        >
                          <option value="mp4">MP4</option>
                          <option value="mov">MOV</option>
                        </select>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <DraftNumberField
                        field="videoFps"
                        label={t.videoOutputFps}
                        value={String(videoOutputFps)}
                        draft={draftOf('videoFps')}
                        note={noteOf('videoFps')}
                        onDraftChange={handleNumericDraftChange}
                        onCommit={commitVideoFpsField}
                      />
                      <label className="block text-xs text-white/70">
                        {t.videoQuality}
                        <select
                          value={videoQualityPreset}
                          onChange={handleVideoQualityPresetChange}
                          className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]"
                        >
                          <option value="draft">{t.qualityDraft}</option>
                          <option value="balanced">{t.qualityBalanced}</option>
                          <option value="high">{t.qualityHigh}</option>
                        </select>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <DraftNumberField
                        field="videoStart"
                        label={t.exportStart}
                        value={String(roundTo(selectedStartSec, 2))}
                        draft={draftOf('videoStart')}
                        note={noteOf('videoStart')}
                        onDraftChange={handleNumericDraftChange}
                        onCommit={commitVideoRangeField}
                      />
                      <DraftNumberField
                        field="videoEnd"
                        label={t.exportEnd}
                        value={videoRangeTillEnd ? '' : String(roundTo(selectedEndSec, 2))}
                        draft={draftOf('videoEnd')}
                        note={noteOf('videoEnd')}
                        placeholder={t.exportTillEnd}
                        onDraftChange={handleNumericDraftChange}
                        onCommit={commitVideoRangeField}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-white/70">
                      <span>{t.exportSound}</span>
                      <Switch
                        checked={exportSoundEnabled}
                        onCheckedChange={setExportSoundEnabled}
                        labelRight=""
                      />
                    </div>
                    </>
                  )}
                </>
              )
            ) : (
              <>
                <label className="block text-xs text-white/70">
                  {t.exportMode}
                  <select value={isPrintMode ? 'print' : 'web'} onChange={handlePrintModeChange} className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]">
                    <option value="web">{t.webImage}</option>
                    <option value="print" disabled={isSourceImageMode}>{t.printGrandFormat}</option>
                  </select>
                </label>
                {isPrintMode ? (
                  <>
                    {!isSourceImageMode && <label className="block text-xs text-white/70">{t.exportTarget}<select value={printTarget} onChange={handlePrintTargetChange} className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]"><option value="area">{t.generationArea}</option><option value="frame">{t.workspaceFrame}</option></select></label>}
                    <label className="block text-xs text-white/70">
                      {t.standardPreset}
                      <select value={printOptions.preset} onChange={handlePrintPresetChange} className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]">
                        {PRINT_PRESETS.map((preset) => <option key={preset.id} value={preset.id}>{preset.label}</option>)}
                        <option value="custom">{t.custom}</option>
                      </select>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <DraftNumberField
                        field="printWidthCm"
                        label={t.widthCm}
                        value={formatCm(printOptions.widthCm)}
                        draft={draftOf('printWidthCm')}
                        preview={numericPreview.fields.printWidthCm}
                        note={noteOf('printWidthCm')}
                        onDraftChange={handleNumericDraftChange}
                        onCommit={commitPrintSizeField}
                      />
                      <DraftNumberField
                        field="printHeightCm"
                        label={t.heightCm}
                        value={formatCm(printOptions.heightCm)}
                        draft={draftOf('printHeightCm')}
                        preview={numericPreview.fields.printHeightCm}
                        note={noteOf('printHeightCm')}
                        onDraftChange={handleNumericDraftChange}
                        onCommit={commitPrintSizeField}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block text-xs text-white/70">{t.dpi}<select value={printOptions.dpi} onChange={(event) => updatePrintOptions({ preset: 'custom', dpi: Number(event.target.value) as PrintExportOptions['dpi'] })} className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]">{PRINT_DPI_VALUES.map((dpi) => <option key={dpi} value={dpi}>{dpi}</option>)}</select></label>
                      <label className="block text-xs text-white/70">{t.format}<select value={printOptions.format} onChange={handlePrintFormatChange} className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]"><option value="tiff">TIFF</option><option value="jpeg" disabled={PRINT_ICC_PROFILES[printOptions.iccProfile].colorSpace === 'cmyk'}>JPEG</option></select></label>
                    </div>
                    <label className="block text-xs text-white/70">{t.iccProfile}<select value={printOptions.iccProfile} onChange={handlePrintIccProfileChange} className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]">{Object.entries(PRINT_ICC_PROFILES).map(([id, profile]) => <option key={id} value={id}>{profile.label}</option>)}</select></label>
                    <label className="block text-xs text-white/70">{t.bleed}<select value={printOptions.bleedMm} onChange={(event) => updatePrintOptions({ bleedMm: Number(event.target.value) as PrintExportOptions['bleedMm'] })} className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]">{PRINT_BLEED_VALUES.map((bleed) => <option key={bleed} value={bleed}>{bleed === 0 ? t.disabled : `${bleed} mm`}</option>)}</select></label>
                    <div className="rounded-md border border-white/10 bg-white/5 px-2 py-2 text-xs text-white/70">Output: <span className={`font-mono ${numericPreview.printDimensions ? 'italic text-white/45' : 'text-white/90'}`}>{shownPrintDimensions.width} × {shownPrintDimensions.height} px</span> · {shownPrintDimensions.widthMm} × {shownPrintDimensions.heightMm} mm</div>
                  </>
                ) : null}
                {!isPrintMode && (<>
                {/* Source-image mode exports THE asset, area/frame is meaningless. */}
                {!isSourceImageMode && (
                  <label className="block text-xs text-white/70">
                    {t.exportTarget}
                    <select
                      value={target}
                      onChange={handleTargetChange}
                      className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]"
                    >
                      <option value="area">{t.generationArea}</option>
                      <option value="frame">{t.workspaceFrame}</option>
                    </select>
                  </label>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs text-white/70">
                    {t.format}
                    <select
                      value={format}
                      onChange={handleFormatChange}
                      className="mt-1 w-full rounded-md border border-[#2A2A2A] bg-[#1A1A1A] px-2 py-2 text-sm text-white outline-none focus:border-[#3B82F6]"
                    >
                      <option value="png">PNG</option>
                      <option value="webp">WebP</option>
                      <option value="jpeg">JPEG</option>
                      {nativeSvgExportAvailable && <option value="svg">SVG</option>}
                      <option value="pdf">PDF</option>
                    </select>
                  </label>

                  <DraftNumberField
                    field="megapixels"
                    label={t.megapixels}
                    value={formatMegapixels(effectiveMegapixels)}
                    draft={draftOf('megapixels')}
                    preview={numericPreview.fields.megapixels}
                    note={noteOf('megapixels')}
                    action={optimalMegapixelsButton}
                    onDraftChange={handleNumericDraftChange}
                    onCommit={commitImageSizeField}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <DraftNumberField
                    field="width"
                    label={t.width}
                    value={String(roundedExportWidth)}
                    draft={draftOf('width')}
                    preview={numericPreview.fields.width}
                    note={noteOf('width')}
                    onDraftChange={handleNumericDraftChange}
                    onCommit={commitImageSizeField}
                  />
                  <DraftNumberField
                    field="height"
                    label={t.height}
                    value={String(roundedExportHeight)}
                    draft={draftOf('height')}
                    preview={numericPreview.fields.height}
                    note={noteOf('height')}
                    onDraftChange={handleNumericDraftChange}
                    onCommit={commitImageSizeField}
                  />
                </div>

                {(format === 'jpeg' || format === 'webp' || format === 'pdf') && (
                  <RangeControl
                    label={`${t.quality}: ${effectiveQuality}%`}
                    min={MIN_QUALITY_PERCENT}
                    max={100}
                    step={1}
                    value={effectiveQuality}
                    onValueChange={setQualityPercent}
                    showValueBox
                  />
                )}

                <div className="flex items-center justify-between text-xs text-white/70">
                  <span>{t.alpha}</span>
                  <Switch
                    checked={effectiveAlpha}
                    disabled={format === 'jpeg' || format === 'pdf'}
                    onCheckedChange={setPreserveAlpha}
                    labelRight=""
                  />
                </div>

                {/* Annotations live on the canvas, not applicable to a source asset. */}
                {!isSourceImageMode && (
                  <div className="flex items-center justify-between text-xs text-white/70">
                    <span>{t.annotations}</span>
                    <Switch
                      checked={includeAnnotations}
                      onCheckedChange={setIncludeAnnotations}
                      labelRight=""
                    />
                  </div>
                )}
                </>)}
              </>
            )}

            {printError && <p className="rounded-md border border-red-400/20 bg-red-400/10 px-2 py-2 text-xs text-red-200">{printError}</p>}
            <div className="sticky bottom-0 z-10 mt-2 flex w-full justify-center gap-2 bg-[#141414] pb-1 pt-2">
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-neutral-600 bg-neutral-700 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDownloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                {t.download} {!isDownloading && !isSourceVideoMode ? `(${downloadSizeLabel})` : ''}
              </button>
              {!isVideoExportMode && !isPrintMode && (
                <button
                  onClick={handleSendToNewProject}
                  disabled={isDownloading || format === 'pdf'}
                  title={t.openInNewProject}
                  aria-label={t.openInNewProject}
                  className="inline-flex aspect-square items-center justify-center rounded-md border border-neutral-600 bg-neutral-700 text-white transition-colors hover:bg-neutral-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <ImagePlus size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageExportModal;
